import type { LngLat } from './types.ts';
import { bearingDeg, distanceM } from './geo.ts';

/**
 * Turn-by-turn instructions from a route line.
 *
 * Apple's first requirement of a CarPlay navigation app is turn-by-turn
 * directions with upcoming maneuvers, so this is not decoration — it is the
 * thing the entitlement is judged on. It is also what makes a route usable
 * without looking at a screen, which matters more on a buggy than in a car
 * because there is no windscreen pillar to mount a phone on.
 *
 * Routers hand back a dense polyline, not instructions. A cart path traced in
 * OpenStreetMap has a vertex every few metres, so the naive approach — one
 * instruction per vertex — produces forty "turn slight left"s for a path that a
 * human would describe as "follow the path round to the green". The work here
 * is deciding which bearing changes are turns and which are the road bending.
 */

export type Maneuver =
  | 'depart'
  | 'arrive'
  | 'straight'
  | 'slight-left'
  | 'left'
  | 'sharp-left'
  | 'slight-right'
  | 'right'
  | 'sharp-right'
  | 'u-turn';

export interface Step {
  /** Where the instruction happens. */
  at: LngLat;
  maneuver: Maneuver;
  /** Metres from the previous step to this one. */
  distanceM: number;
  /** Signed turn angle, degrees. Negative is left. */
  turnDeg: number;
  /** Spoken form, e.g. "In 60 metres, turn left". */
  text: string;
}

/**
 * Below this, a bearing change is the path curving rather than a junction.
 *
 * 25 degrees is deliberately generous. Cart paths wind constantly; treating
 * every gentle sweep as a turn is what makes machine-generated directions
 * unusable, and a golfer who is told to "turn right" on a path that merely
 * bends stops trusting the next instruction too.
 */
const TURN_THRESHOLD_DEG = 25;

/**
 * Look this far along the line when measuring a turn.
 *
 * Comparing one segment to the next measures vertex noise, not the shape of
 * the junction — on a dense polyline two consecutive segments are almost
 * parallel even at a right-angle corner, because the corner itself is rounded
 * into several vertices. Measuring across a window recovers the turn a person
 * would actually see.
 */
const WINDOW_M = 18;

/** Smallest gap between instructions. Two turns 5 m apart is one instruction. */
const MIN_STEP_GAP_M = 12;

function classify(turnDeg: number): Maneuver {
  const a = Math.abs(turnDeg);
  if (a >= 150) return 'u-turn';
  if (a < TURN_THRESHOLD_DEG) return 'straight';
  const side = turnDeg < 0 ? 'left' : 'right';
  if (a < 50) return `slight-${side}` as Maneuver;
  if (a < 110) return side as Maneuver;
  return `sharp-${side}` as Maneuver;
}

/** Signed difference between two bearings, in (-180, 180]. Negative is left. */
export function bearingDelta(from: number, to: number): number {
  let d = ((to - from + 540) % 360) - 180;
  // (-180, 180]: a perfect reversal reads as a right u-turn rather than -180,
  // which would otherwise flip sign on floating-point noise.
  if (d === -180) d = 180;
  return d;
}

/** The point `metres` further along the line from index `i`, and its bearing. */
function bearingAhead(line: LngLat[], i: number, metres: number): number | null {
  let travelled = 0;
  for (let j = i; j < line.length - 1; j++) {
    travelled += distanceM(line[j], line[j + 1]);
    if (travelled >= metres) return bearingDeg(line[i], line[j + 1]);
  }
  return line.length - 1 > i ? bearingDeg(line[i], line[line.length - 1]) : null;
}

function bearingBehind(line: LngLat[], i: number, metres: number): number | null {
  let travelled = 0;
  for (let j = i; j > 0; j--) {
    travelled += distanceM(line[j], line[j - 1]);
    if (travelled >= metres) return bearingDeg(line[j - 1], line[i]);
  }
  return i > 0 ? bearingDeg(line[0], line[i]) : null;
}

export interface StepOptions {
  /** What the route arrives at, for the final instruction. */
  destination?: string;
  unit?: 'm' | 'yd';
  /**
   * Most golfers walk. A walker reads instructions at a tenth of the speed a
   * buggy covers ground, needs them far later, and turns on the spot — so the
   * same route wants different wording and different lead distances depending
   * on how it is being travelled.
   */
  mode?: 'drive' | 'walk';
}

export function describe(maneuver: Maneuver, destination?: string, mode: 'drive' | 'walk' = 'drive'): string {
  switch (maneuver) {
    case 'depart': return mode === 'walk' ? 'Set off' : 'Head off';
    case 'arrive': return destination ? `Arrive at ${destination}` : 'Arrive';
    case 'straight': return 'Continue straight';
    case 'slight-left': return 'Bear left';
    case 'left': return 'Turn left';
    case 'sharp-left': return 'Turn sharp left';
    case 'slight-right': return 'Bear right';
    case 'right': return 'Turn right';
    case 'sharp-right': return 'Turn sharp right';
    case 'u-turn': return mode === 'walk' ? 'Turn back' : 'Make a U-turn';
  }
}

/**
 * Rounded the way a person says it: to 10 below 100, to 50 above.
 *
 * On foot the rounding stays fine all the way up — "in 250 metres" is a useless
 * instruction to someone walking, who wants "in 40 metres" and will get there
 * in half a minute.
 */
export function spokenDistance(
  metres: number,
  unit: 'm' | 'yd' = 'm',
  mode: 'drive' | 'walk' = 'drive'
): string {
  const v = unit === 'yd' ? metres / 0.9144 : metres;
  const u = unit === 'yd' ? 'yards' : 'metres';
  if (v < (mode === 'walk' ? 8 : 20)) return 'Now';
  const coarse = mode === 'walk' ? 200 : 100;
  const rounded = v < coarse ? Math.round(v / 10) * 10 : Math.round(v / 50) * 50;
  return `In ${rounded} ${u}`;
}

/**
 * Turn-by-turn steps for a route line.
 *
 * Always begins with `depart` and ends with `arrive`, so a caller can present a
 * complete journey without special-casing the ends.
 */
export function toSteps(line: LngLat[], opts: StepOptions = {}): Step[] {
  const clean = line.filter((p, i) => i === 0 || distanceM(p, line[i - 1]) > 0.5);
  if (clean.length < 2) return [];

  const unit = opts.unit ?? 'm';
  const mode = opts.mode ?? 'drive';
  // A walker turns on the spot and can act on instructions much closer
  // together, so the minimum gap shrinks with the mode.
  const minGap = mode === 'walk' ? 6 : MIN_STEP_GAP_M;
  const steps: Step[] = [];
  let sinceLast = 0;

  const push = (at: LngLat, maneuver: Maneuver, turnDeg: number, dist: number) => {
    const text =
      maneuver === 'depart' || maneuver === 'arrive'
        ? describe(maneuver, opts.destination, mode)
        : `${spokenDistance(dist, unit, mode)}, ${describe(maneuver, undefined, mode).toLowerCase()}`;
    steps.push({ at, maneuver, distanceM: Math.round(dist), turnDeg: Math.round(turnDeg), text });
  };

  push(clean[0], 'depart', 0, 0);

  // Turn angle at every interior vertex, measured across the window.
  const turns: (number | null)[] = clean.map((_, i) => {
    if (i === 0 || i === clean.length - 1) return null;
    const before = bearingBehind(clean, i, WINDOW_M);
    const after = bearingAhead(clean, i, WINDOW_M);
    return before == null || after == null ? null : bearingDelta(before, after);
  });

  /**
   * One instruction per corner, at its sharpest point.
   *
   * A right-angle junction is not one vertex — OSM rounds it into several, and
   * every vertex within the measurement window of the corner reports the same
   * turn. Emitting on the first one crossing the threshold gave two "turn
   * right"s for one corner, the second arriving after the driver had already
   * turned. So candidates are grouped into a cluster and the apex wins.
   */
  let i = 1;
  while (i < clean.length - 1) {
    const t = turns[i];
    if (t == null || classify(t) === 'straight') {
      sinceLast += distanceM(clean[i - 1], clean[i]);
      i++;
      continue;
    }

    // Extend the cluster while the turn persists, tracking the sharpest vertex.
    let apex = i;
    let j = i;
    let spanned = 0;
    while (j < clean.length - 1) {
      const tj = turns[j];
      if (tj == null || classify(tj) === 'straight') break;
      if (Math.abs(tj) > Math.abs(turns[apex]!)) apex = j;
      spanned += distanceM(clean[j], clean[j + 1]);
      j++;
    }

    const runUp = sinceLast + (() => {
      let d = 0;
      for (let k = i; k < apex; k++) d += distanceM(clean[k], clean[k + 1]);
      return d;
    })();

    if (runUp >= minGap || steps.length === 1) {
      push(clean[apex], classify(turns[apex]!), turns[apex]!, runUp);
      sinceLast = 0;
      // Distance from the apex to the end of the cluster still has to count.
      for (let k = apex; k < j; k++) sinceLast += distanceM(clean[k], clean[k + 1]);
    } else {
      sinceLast += spanned;
    }
    i = j;
  }

  // The loop stops one short of the end, so the final segment is added here.
  const end = clean[clean.length - 1];
  sinceLast += distanceM(clean[clean.length - 2], end);
  push(end, 'arrive', 0, sinceLast);
  return steps;
}

/** Total length of a route line, metres. */
export function routeLength(line: LngLat[]): number {
  let total = 0;
  for (let i = 0; i < line.length - 1; i++) total += distanceM(line[i], line[i + 1]);
  return total;
}

/**
 * The step a traveller is currently working towards, and how far to it.
 *
 * Nearest-step-ahead rather than nearest-step: standing ON a junction, the
 * instruction that matters is the next one, not the one just completed.
 */
const clamp01 = (t: number) => (t < 0 ? 0 : t > 1 ? 1 : t);

/** Fraction (0..1) of the way from a to b that p projects onto, in a local
 *  metres plane so it is accurate at road scale. */
function projFraction(a: LngLat, b: LngLat, p: LngLat): number {
  const mx = Math.cos((a[1] * Math.PI) / 180) * 111_320;
  const my = 110_540;
  const bx = (b[0] - a[0]) * mx;
  const by = (b[1] - a[1]) * my;
  const px = (p[0] - a[0]) * mx;
  const py = (p[1] - a[1]) * my;
  const len2 = bx * bx + by * by;
  return len2 === 0 ? 0 : (px * bx + py * by) / len2;
}

/** Distance in metres from the route start to where `pos` projects onto it. */
function progressAlong(line: LngLat[], pos: LngLat): number {
  let cum = 0;
  let best = { d: Infinity, prog: 0 };
  for (let i = 0; i < line.length - 1; i++) {
    const a = line[i];
    const b = line[i + 1];
    const seg = distanceM(a, b);
    const t = clamp01(projFraction(a, b, pos));
    const proj: LngLat = [a[0] + (b[0] - a[0]) * t, a[1] + (b[1] - a[1]) * t];
    const d = distanceM(pos, proj);
    if (d < best.d) best = { d, prog: cum + seg * t };
    cum += seg;
  }
  return best.prog;
}

/**
 * The next instruction to show.
 *
 * With a `line`, it is the next maneuver AHEAD ALONG THE ROUTE — position and
 * every step are projected onto the route and compared by distance travelled,
 * not by straight-line proximity. The two disagree wherever the route runs back
 * near itself, most sharply at a motorway interchange where the exit ramp lies
 * a few metres from the surface streets it feeds: as-the-crow-flies would
 * announce a street you reach kilometres later instead of the exit in front of
 * you. Without a `line` it falls back to nearest-ahead, which is correct on a
 * spread-out cart network and is what the unit tests pin.
 */
export function upcoming(
  steps: Step[],
  pos: LngLat,
  line?: LngLat[]
): { step: Step; distanceM: number } | null {
  if (line && line.length >= 2) {
    const here = progressAlong(line, pos);
    let best: { step: Step; distanceM: number } | null = null;
    for (const s of steps) {
      if (s.maneuver === 'depart') continue;
      const ahead = progressAlong(line, s.at) - here;
      if (ahead < -8) continue; // already passed, with a little jitter tolerance
      if (!best || ahead < best.distanceM) best = { step: s, distanceM: Math.max(0, ahead) };
    }
    if (best) return best;
  }
  let best: { step: Step; distanceM: number } | null = null;
  for (const s of steps) {
    if (s.maneuver === 'depart') continue;
    const d = distanceM(pos, s.at);
    if (!best || d < best.distanceM) best = { step: s, distanceM: d };
  }
  return best;
}
